import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trash2, Ban, Warehouse, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { WasteMealsSection } from "@/components/waste-meals/WasteMealsSection";
import { CancellationsSection } from "@/components/cancellations/CancellationsSection";
import { StockCountSection } from "@/components/stock-count/StockCountSection";
import { DailyCountSection } from "@/components/daily-count/DailyCountSection";

const H = OPS.branchOps;

const SECTION_DEFS = {
    wm: { icon: Trash2, subtitle: "wm_subtitle", render: () => <WasteMealsSection /> },
    co: { icon: Ban, subtitle: "co_subtitle", render: (section) => <CancellationsSection section={section} /> },
    sc: { icon: Warehouse, subtitle: "stk_subtitle", render: (section) => <StockCountSection section={section} /> },
    dk: { icon: ClipboardCheck, subtitle: "dk_subtitle", render: (section) => <DailyCountSection section={section} /> },
};

// Phones: up to three sections share one row (grid-flow-col auto-cols-fr), so
// the label drops to text-xs and may wrap onto a second line instead of being
// cut; four sections sit in a 2 × 2 grid (one row of four from `sm` up). From
// `sm` up each label is one line, truncated as before.
const switchLayout = (n) => (n >= 4
    ? "grid grid-cols-2 sm:inline-grid sm:grid-cols-4"
    : "grid auto-cols-fr grid-flow-col sm:inline-grid");

// The pickers of a section (?loc=&period=&date=&month=) belong to that section only.
const SECTION_PARAMS = ["tab", "loc", "period", "date", "month"];

const switchClass = (on) =>
    `inline-flex min-h-10 min-w-0 items-center justify-center gap-1.5 rounded-full px-2 py-1 text-xs font-bold leading-tight transition-[color,background-color] duration-150 ease-out sm:h-10 sm:gap-2 sm:px-3 sm:py-0 sm:text-sm ${
        on
            ? "bg-[color:var(--brand-brown)] text-white shadow-[var(--shadow-sm)]"
            : "text-[color:var(--ink-muted)] hover:bg-[color:var(--brand-orange-soft)] hover:text-[color:var(--ink)]"
    }`;

/**
 * «عمليات الفرع» — the two-level branch hub (message 16 §3). Sections and
 * their tab lists come from `GET /branch-ops/scope`, computed on the server
 * from the caller's capabilities; this shell renders what it is given and
 * decides nothing. `?section=` defaults to `wm`; every Waste & Meals URL
 * (`?tab=`), test id and behaviour is unchanged inside the shell.
 */
export default function WasteMealsHub() {
    const { t, tn } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const [hub, setHub] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/branch-ops/scope")
            .then((r) => { if (alive) setHub(r.data); })
            .catch(() => { if (alive) setHub(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const visible = useMemo(
        () => (hub?.sections || []).filter((s) => s.visible && SECTION_DEFS[s.key]),
        [hub],
    );

    const requested = searchParams.get("section");
    const active = visible.some((s) => s.key === requested)
        ? requested
        : (visible.some((s) => s.key === "wm") ? "wm" : visible[0]?.key);

    const onSectionChange = (key) => {
        const next = new URLSearchParams(searchParams);
        next.set("section", key);
        // the stock count's (?loc=&period=) and the daily count's (?loc=&date=&month=)
        // pickers belong to their own section only
        SECTION_PARAMS.forEach((p) => next.delete(p));
        setSearchParams(next, { replace: true });
    };

    if (loading) return <InlineLoader label={t("loading")} />;

    // No visible section (no branch, no capability): the W&M section renders
    // its own «no branch» note exactly as it did before the shell existed.
    if (!active) return <WasteMealsSection />;

    const section = visible.find((s) => s.key === active);
    const def = SECTION_DEFS[active];
    const title = hub?.label ? tn(hub.label) : t("wm_title");

    return (
        <div data-testid={H.hub} data-section={active} className="space-y-5">
            <PageHeader icon={def.icon} title={title} subtitle={t(def.subtitle)} />
            {visible.length > 1 ? (
                <div data-testid={H.sectionSwitch} role="tablist"
                     className={`${switchLayout(visible.length)} gap-1 rounded-[1.75rem] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:rounded-full`}>
                    {visible.map((s) => {
                        const Icon = SECTION_DEFS[s.key].icon;
                        const on = s.key === active;
                        return (
                            <button key={s.key} type="button" role="tab" aria-selected={on}
                                    data-testid={H.section(s.key)} data-active={on ? "true" : "false"}
                                    onClick={() => onSectionChange(s.key)} className={switchClass(on)}>
                                <Icon className="h-4 w-4 shrink-0" />
                                <span className="line-clamp-2 min-w-0 text-center sm:line-clamp-none sm:truncate sm:text-start">{tn(s)}</span>
                            </button>
                        );
                    })}
                </div>
            ) : null}
            {def.render(section)}
        </div>
    );
}
